// 人員與存取（SPEC #vms-web-220、#vms-web-756、#vms-web-787；VMS UX 評審 2026-10-13 P0-9）：五個分頁
//   成員（/people/members）｜存取權（/people/access，預設）｜角色（/people/roles）｜對外（/people/external）｜成員群組（/people/member-groups）
//   - 存取權：統一清單（人 / 群組 / 另一個組織 × 節點 / 家戶 / 設備 × 能做什麼 × 來源 × 期限），〔＋ 給存取權〕四步精靈；取代原本的
//     授權、家戶成員、分享三個分頁與組織結構頁的節點指派表（舊網址轉址到這裡並帶來源篩選，features/shell/nav.ts）
//   - 成員抽屜「<名字> 能做什麼」：任何分頁網址帶 ?member=<user_id> 就開（全域搜尋找到人也是這條）
//   - 對外：委派出去、委派給本組織、分享給本組織三段同一頁；只限 team 組織
//   - 只有家戶角色、沒有組織管理權的人：只看到「存取權」（他管的家戶的家戶成員與分享）
// 組織一次只管一個 = 頁首範圍所在的組織，頁內不另放組織下拉（P0-3）。授權變更伺服器要求 5 分鐘內重新驗證，SDK 自動跳對話框。
import { Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { hasOrgPerm, isOrgAdmin, orgApi, orgUsable, type MyOrg } from '../../lib/planeApi';
import { gateOrg, OrgCapsule, OrgChooser, relevantOrgs } from '../shell/OrgGate';
import { MembersTab, useMemberActions } from './MembersTab';
import { RolesTab } from './RolesTab';
import { HomeMembersTab } from './HomeMembersTab';
import { SharesTab } from './SharesTab';
import { OrgSharesTab } from './OrgSharesTab';
import { MemberGroupsTab } from '../orgtree/MemberGroupsTab';
import { DelegationsTab } from '../orgtree/DelegationsTab';
import { AccessList } from './AccessList';
import { MemberDrawer } from './MemberDrawer';

function drawerButtons(items: { key: string; label: React.ReactNode; danger?: boolean; onClick(): void }[]) {
  if (!items.length) return null;
  return (
    <>
      {items.map((i) => (
        <button key={i.key} className={'btn btn-sm' + (i.danger ? ' btn-danger-outline' : '')} onClick={i.onClick}>
          {i.label}
        </button>
      ))}
    </>
  );
}

export function PeoplePage() {
  const { t } = useTranslation();
  const { org, orgs, homes } = useScope();
  const managed = relevantOrgs(orgs, homes).filter((o) => orgUsable(o) && (isOrgAdmin(o) || hasOrgPerm(o, 'org.sites')));
  const current = gateOrg(org, managed);
  // 範圍是「全部」又能管不只一個組織：先問要管理哪個（頁面其他內容等選了再出現）
  if (!current && managed.length > 1)
    return (
      <div className="page">
        <h1 className="page-title">{t('nav.people')}</h1>
        <OrgChooser candidates={managed} />
      </div>
    );
  if (!current) return <HomeOnly />;
  return <OrgPeople org={current} candidates={managed} />;
}

function OrgPeople({ org, candidates }: { org: MyOrg; candidates: MyOrg[] }) {
  const { t } = useTranslation();
  const team = org.kind === 'team';
  const admin = isOrgAdmin(org);
  // 對外與成員群組只限 team 組織的 owner / org_admin（SPEC #org-tree-delegation「誰能委派」、#org-tree-api member-groups）
  const external = team && admin;
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org) });
  const drawerActs = useMemberActions(org, { inDrawer: true });
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.people')}</h1>
      <OrgCapsule current={org} candidates={candidates} />
      <div className="tabs" role="tablist">
        <NavLink to="/people/members" role="tab">
          {t('people.members')}
        </NavLink>
        <NavLink to="/people/access" role="tab">
          {t('iam.tab_access')}
        </NavLink>
        <NavLink to="/people/roles" role="tab">
          {t('people.roles')}
        </NavLink>
        {team && (
          <NavLink to="/people/external" role="tab">
            {t('iam.tab_external')}
          </NavLink>
        )}
        {external && (
          <NavLink to="/people/member-groups" role="tab">
            {t('ot.member_groups')}
          </NavLink>
        )}
      </div>
      <Routes>
        <Route index element={<Navigate to="access" replace />} />
        <Route path="members" element={<MembersTab org={org} />} />
        <Route path="access" element={<AccessList org={org} filters />} />
        <Route path="roles" element={<RolesTab org={org} />} />
        {team && <Route path="external/*" element={<ExternalTab org={org} admin={external} />} />}
        {external && <Route path="member-groups" element={<MemberGroupsTab org={org} />} />}
        <Route path="*" element={<Navigate to="/people/access" replace />} />
      </Routes>
      <MemberDrawer
        org={org}
        members={members.data}
        actions={(m) => drawerButtons(drawerActs.items(m).filter((i) => !i.hidden && !i.maintenance))}
        maintenance={(m) => drawerButtons(drawerActs.items(m).filter((i) => !i.hidden && i.maintenance))}
      />
      {drawerActs.dialogs}
    </div>
  );
}

/** 對外：委派出去、委派給本組織（team 的 owner / org_admin）、分享給本組織，三段同一頁，每段一句話說差別。 */
function ExternalTab({ org, admin }: { org: MyOrg; admin: boolean }) {
  const { t } = useTranslation();
  const shares = (
    <div style={{ marginTop: 12 }} data-testid="ext-org-shares" id="ext-shares" className="anchor-target">
      <h2 className="section-title">{t('iam.ext_shares_title')}</h2>
      <p className="muted small">{t('iam.ext_shares_desc')}</p>
      <OrgSharesTab org={org} />
    </div>
  );
  return <DelegationsTab org={org} extra={shares} delegations={admin} />;
}

/** 只有家戶角色（家戶 owner / admin）、沒有組織管理權：只看自己管的家戶的家戶成員與分享（SPEC #vms-web-787「人員與存取的分頁」）。 */
function HomeOnly() {
  const { t } = useTranslation();
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.people')}</h1>
      <div className="tabs" role="tablist">
        <span className="active" role="tab" aria-selected>
          {t('iam.tab_access')}
        </span>
      </div>
      <HomeMembersTab />
      <SharesTab />
    </div>
  );
}
